import { Injectable } from '@angular/core';
import { Observable, filter, map, take } from 'rxjs';
import { HTTPService } from 'src/app/services/http.service';
import { UserAuthenticationServices } from 'src/app/services/user-authentication.services';
import { TicketAttachment } from '../models/chamados.models';

/**
 * Sobe o arquivo e devolve o anexo pronto para o chamado.
 *
 * POR QUE O S3, e não o `JsonBrokerServices/uploadfile`: aquele grava o blob
 * numa COLUNA de uma entidade SQL (`entityName` + `datafield` + `condition`),
 * e o chamado vive no Mongo — não há coluna para apontar. O
 * `AmazonServices/uploadfile` devolve uma URL, que é exatamente o que o
 * `TicketAttachment.ref` espera: a conversa já transforma `ref` começando com
 * `http` em link, e o que não é endereço aparece só como nome.
 *
 * O Java não precisa de mudança nenhuma: ele grava `anexos` como recebe.
 *
 * O QUE ISSO IMPLICA, e é decisão de quem opera: o arquivo passa a viver no
 * bucket, fora do backup do banco, e a URL é o único ponteiro para ele.
 * Retenção e expurgo são do bucket, não do módulo.
 */
@Injectable({ providedIn: 'root' })
export class AnexosService {

    /**
     * Teto por arquivo.
     *
     * Não é limite do servidor: é o ponto em que subir pelo navegador começa a
     * falhar em rede ruim, e falhar DEPOIS de o cliente ter escrito a mensagem
     * é o pior momento possível.
     */
    readonly tamanhoMaximo = 20 * 1024 * 1024;

    constructor(
        private http: HTTPService,
        private uas: UserAuthenticationServices
    ) { }

    /** O arquivo cabe? */
    cabe(arquivo: File): boolean {
        return arquivo != null && arquivo.size <= this.tamanhoMaximo;
    }

    /**
     * A pasta do chamado no bucket: `chamados/TKT-23_66f1...`.
     *
     * O numero para a pessoa achar, o id para nao haver duas pastas com o
     * mesmo nome (o numero e por empresa, e repete entre empresas).
     */
    pastaDoChamado(numero: string, ticketId: string): string {
        return 'chamados/' + (numero ?? 'sem-numero') + '_' + (ticketId ?? '');
    }

    /**
     * A pasta de quem ainda esta ABRINDO o chamado.
     *
     * Na abertura o chamado ainda nao existe — nao ha numero para usar. Mover
     * o objeto depois seria copiar e apagar no S3, e nao vale arriscar perder
     * anexo por causa de organizacao.
     */
    pastaDaAbertura(): string {
        return 'chamados/abertura/' + (this.uas.user?.iduser ?? 'anonimo');
    }

    /**
     * Sobe UM arquivo.
     *
     * O nome e o tamanho vêm do próprio arquivo, e não da resposta: o que
     * interessa na conversa é o que a pessoa reconhece ("boleto.pdf"), e não o
     * nome que o bucket gerou.
     *
     * PELO `TicketServices/uploadAnexo`, e não pelo `AmazonServices/uploadfile`:
     * aquele passa o nome por um SHA-256 (`generateUniqueFilename`), o que
     * apaga a pasta, apaga a extensão e — pior — faz dois arquivos de mesmo
     * nome virarem a MESMA chave, um sobrescrevendo o outro em silêncio. Ver
     * `docs/chamados/java-proposto/TicketServices-anexo.java.txt`.
     */
    enviar(arquivo: File, pasta: string): Observable<TicketAttachment> {

        const formData = new FormData();
        formData.append('file', arquivo);
        formData.append('FILENAME', arquivo.name);
        formData.append('PASTA', pasta);
        // Sem CODECOMPANY: a credencial do bucket e do MODULO
        // (`temapi.ticket.attach.storage`), e nao da empresa de quem esta
        // logado — quem abre chamado e do cliente, e o cliente nao tem bucket.
        formData.append('IDUSER', String(this.uas.user?.iduser ?? ''));

        return this.http.postFile(formData, 'TicketServices/uploadAnexo', false, true, false).pipe(
            // O `postFile` emite VARIAS vezes, e nem toda emissao e a
            // resposta:
            //
            //   - `{ status: 'progress' }` a cada pedaco enviado;
            //   - a STRING `"Unhandled event: 0"` para os eventos que ele nao
            //     trata (o `Sent`, que vem primeiro);
            //   - e, por fim, o corpo da resposta.
            //
            // Sem filtrar as duas primeiras, o anexo era montado a partir da
            // string — `resultado` vinha `undefined`, o `ref` ficava nulo e a
            // conversa mostrava "anexo sem link".
            filter((resposta: any) => resposta != null
                && typeof resposta === 'object'
                && resposta['status'] !== 'progress'),
            take(1),
            map((resposta: any) => {

                // A CHAVE, e nao a URL.
                //
                // Anexo de chamado e dado de cliente: guardar a URL publica
                // faria o arquivo ficar acessivel para sempre a quem tivesse o
                // link, sem passar por login. Guardando a chave, o link e
                // assinado na hora de abrir, dura minutos, e so sai para quem
                // pode ver aquele chamado.
                //
                // O `resultado` (URL) continua sendo aceito para os anexos que
                // subiram antes desta mudanca.
                const chave = resposta?.['chave'] ?? resposta?.['resultado'];

                // Sem referencia nao ha anexo: um item na conversa que nao
                // abre e pior que o erro — a pessoa acha que mandou o arquivo.
                if (chave == null || String(chave).trim().length === 0) {
                    throw new Error('upload sem referencia de retorno');
                }

                return {
                    nome: arquivo.name,
                    mime: arquivo.type,
                    tamanho: arquivo.size,
                    ref: chave,
                } as TicketAttachment;
            })
        );
    }
}
